import { ScrollView, View, Text } from 'react-native';
import { DataTable } from 'react-native-paper';
import ControlStyles from './ControlStyles';
import ButtonControl from './Button';
import DataTableCustomWithFilter from './DataTableCustomWithFilter';
import React, { useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnDef,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Toolbar,
  Button,
} from "@mui/material";
import { handleExportClick } from "../../ConsolidatedLogs/ConsolidatedController";

interface Props {
  data: Array<any>;
  columns: Array<any>;
  siteId: string;
  logTitle: string;
  tableTitle?: string;
  tableId?: string;
}

const CustomDT: React.FC<Props> = (props) => {
  // 1. Build columns from props
  const columns = useMemo<ColumnDef<any>[]>(
    () =>
      props.columns
        .filter((col: any) => col.hidden !== true) // only visible
        .map((col: any) => ({
          accessorKey: col.ColumnName,
          header: col.ColumnName,
          cell: (info) => info.getValue(),
        })),
    [props.columns]
  );

  // 2. Setup table
  const table = useReactTable({
    data: props.data,
    columns,
    initialState: {
      pagination: { pageSize: 12 }, // ✅ default page length = 12
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  // 3. Export handler
  const jsonToExcelExport = (filename: string) => {
    const filteredData = table.getRowModel().rows.map((row) => {
      const rowData: Record<string, any> = {};
      columns.forEach((col: any) => {
        if (["ID", "Attachments"].includes(col.accessorKey)) return;
        rowData[col.accessorKey] = row.getValue(col.accessorKey);
      });
      return rowData;
    });
    handleExportClick(filteredData, filename);
  };
  const buildExportRows = (rows: any[]) => {
    return rows.map((row) => {
      const rowData: Record<string, any> = {};
      columns.forEach((col: any) => {
        if (["ID", "Attachments"].includes(col.accessorKey)) return;
        rowData[col.accessorKey] = row.getValue(col.accessorKey);
      });
      return rowData;
    });
  };

  const exportPage = (filename: string) => {
    const rows = table.getPaginationRowModel().rows; // ✅ current page
    handleExportClick(buildExportRows(rows), filename);
  };

  const exportFilteredAllPages = (filename: string) => {
    const rows = table.getPrePaginationRowModel().rows; // ✅ filtered/sorted, not paginated
    handleExportClick(buildExportRows(rows), filename);
  };

  const exportAllRaw = (filename: string) => {
    // ✅ raw data from props (ignores filters/sorting)
    const raw = props.data.map((item) => {
      const rowData: Record<string, any> = {};
      columns.forEach((col: any) => {
        if (["ID", "Attachments"].includes(col.accessorKey)) return;
        rowData[col.accessorKey] = item[col.accessorKey];
      });
      return rowData;
    });
    handleExportClick(raw, filename);
  };


  // 4. Handle no data
  if (!props.data || props.data.length === 0) {
    return (
      <Toolbar
        sx={{
          height: 40,
          display: "flex",
          alignItems: "center",
          fontWeight: "bold",
        }}
      >
        {props.tableTitle} : Oops! No records found
      </Toolbar>
    );
  }
  const pageRows = table.getPaginationRowModel().rows.length;              // rows on current page
  const filteredRows = table.getFilteredRowModel().rows.length;            // rows after filters (even if you add later)
  const totalRows = props.data.length;

  const toNumber = (v: any) => {
    if (v === null || v === undefined) return 0;
    if (typeof v === "number") return Number.isFinite(v) ? v : 0;

    // handle "1,234" / "12.5" / " 7 "
    const n = parseFloat(String(v).replace(/,/g, "").trim());
    return Number.isFinite(n) ? n : 0;
  };

  // ✅ totals should reflect ALL rows (not just current page)
  // and later it will reflect filters/sorting automatically.
  const totalsByColumn = useMemo(() => {
    const totals: Record<string, number> = {};

    // Use TanStack row model (pre-pagination) so totals aren't limited to page size
    const rows = table.getPrePaginationRowModel().rows;

    columns.forEach((col: any) => {
      const key = col.accessorKey;
      if (!key) return;

      // skip non-total columns
      if (["ID", "Attachments"].includes(key)) return;

      let sum = 0;
      let hasAnyNumeric = false;

      for (const r of rows) {
        const val = r.getValue(key);
        const num = toNumber(val);

        // detect numeric-ish values: if conversion changes something meaningful
        if (val !== "" && val !== null && val !== undefined && !Number.isNaN(num)) {
          // if it's truly numeric-ish, count it
          if (typeof val === "number" || /^[\s\d,.\-]+$/.test(String(val))) {
            hasAnyNumeric = true;
            sum += num;
          }
        }
      }

      // only store totals for columns that look numeric
      if (hasAnyNumeric) totals[key] = sum;
    });

    return totals;
  }, [table, columns]);

  // 5. Render table with header + export button
  return (
    <Paper>
      {/* Header */}
      <Toolbar
        sx={{
          height: 40,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ fontWeight: "bold" }}>{props.tableTitle}</span>
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#4BB75F",
            "&:disabled": { backgroundColor: "#676767" },
          }}
          onClick={() =>
            exportFilteredAllPages(props.tableTitle && `${props.tableTitle}`)
          }
          disabled={props.data.length === 0}
        >
          Export
        </Button>
      </Toolbar>

      {/* Table */}
      <TableContainer sx={{ maxHeight: 500 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {table.getHeaderGroups().map((hg) =>
                hg.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      backgroundColor: "#000000",
                      color: "#ffffff",
                      fontWeight: "bold",
                    }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </TableCell>
                ))
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    sx={{
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>

            ))}
            <TableRow
              sx={{
                position: "sticky",
                bottom: 0,
                backgroundColor: "#f5f5f5",
                zIndex: 1,
              }}
            >
              {table.getVisibleLeafColumns().map((col, index) => {
                const key = col.id; // matches accessorKey in your setup

                // First visible column shows label
                if (index === 0) {
                  return (
                    <TableCell key={col.id} sx={{ fontWeight: "bold" }}>
                      Total
                    </TableCell>
                  );
                }

                // Show total if numeric column, otherwise blank
                const total = totalsByColumn[key];
                return (
                  <TableCell key={col.id} sx={{ fontWeight: "bold" }}>
                    {total !== undefined ? total : ""}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Toolbar
        sx={{ display: "flex", justifyContent: "space-between", padding: 2 }}
      >
        <div>
          <Button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Prev
          </Button>
          <span style={{ margin: "0 8px" }}>
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </span>
          <Button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
        <div>
          Showing <b>{pageRows}</b> of <b>{filteredRows}</b> filtered (total <b>{totalRows}</b>)
        </div>
      </Toolbar>
    </Paper>
  );
};

export default CustomDT;
